export interface DateRange {
  from: Date;
  to: Date;
}

const getISOWeekStart = (week: number, year: number): Date => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const monday = new Date(simple);
  monday.setDate(simple.getDate() - ((dow + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return monday;
};

export const getISOWeekNumber = (date: Date): number => {
  const start = new Date(date.getFullYear(), 0, 1);
  return Math.ceil(((+date - +start) / 86_400_000 + start.getDay() + 1) / 7);
};

/**
 * Computes default date range from start of current month to end of current week
 */
export const computeDefaultDateRangeMonthToCurrentWeek = (): DateRange => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentWeek = getISOWeekNumber(now);
  const startOfMonth = new Date(currentYear, now.getMonth(), 1);
  const endOfMonth = new Date(currentYear, now.getMonth() + 1, 0);

  const defaultStart = getISOWeekNumber(startOfMonth);
  const defaultEnd = Math.min(getISOWeekNumber(endOfMonth), currentWeek);

  const from = getISOWeekStart(defaultStart, currentYear);
  const to = getISOWeekStart(defaultEnd, currentYear);
  to.setDate(to.getDate() + 6);
  to.setHours(23, 59, 59, 999);

  return { from, to };
};

export const isValidDateRange = (range: DateRange | null): range is DateRange => {
  return (
    range !== null &&
    range.from instanceof Date &&
    range.to instanceof Date &&
    !isNaN(range.from.getTime()) &&
    !isNaN(range.to.getTime()) &&
    range.from <= range.to
  );
};

export const coerceDateRange = (range: Partial<DateRange> | null): DateRange => {
  if (range && isValidDateRange(range as DateRange)) {
    return range as DateRange;
  }
  return computeDefaultDateRangeMonthToCurrentWeek();
};

/**
 * Rolling date windows anchored to a reference date (defaults to "now").
 * Used by scheduled reports so a saved window keeps producing a fresh range on every run,
 * instead of the absolute dateFrom/dateTo baked into a saved search.
 */
export enum RelativeDateWindow {
  Last7Days = 'Last7Days',
  Last14Days = 'Last14Days',
  Last30Days = 'Last30Days',
  LastWeek = 'LastWeek',
  LastMonth = 'LastMonth',
  LastQuarter = 'LastQuarter',
  MonthToDate = 'MonthToDate',
  QuarterToDate = 'QuarterToDate',
  YearToDate = 'YearToDate',
  CurrentYear = 'CurrentYear',
  Last2Years = 'Last2Years',
  Last5Years = 'Last5Years'
}

export const RelativeDateWindowTranslations: Record<RelativeDateWindow, string> = {
  [RelativeDateWindow.Last7Days]: 'f-report-window-last-7-days',
  [RelativeDateWindow.Last14Days]: 'f-report-window-last-14-days',
  [RelativeDateWindow.Last30Days]: 'f-report-window-last-30-days',
  [RelativeDateWindow.LastWeek]: 'f-report-window-last-week',
  [RelativeDateWindow.LastMonth]: 'f-report-window-last-month',
  [RelativeDateWindow.LastQuarter]: 'f-report-window-last-quarter',
  [RelativeDateWindow.MonthToDate]: 'f-report-window-month-to-date',
  [RelativeDateWindow.QuarterToDate]: 'f-report-window-quarter-to-date',
  [RelativeDateWindow.YearToDate]: 'f-report-window-year-to-date',
  [RelativeDateWindow.CurrentYear]: 'f-report-window-current-year',
  [RelativeDateWindow.Last2Years]: 'f-report-window-last-2-years',
  [RelativeDateWindow.Last5Years]: 'f-report-window-last-5-years'
};

const startOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const endOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const addYears = (date: Date, years: number, extraDays = 0): Date => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  result.setDate(result.getDate() + extraDays);
  return result;
};

const startOfISOWeek = (date: Date): Date => {
  const result = new Date(date);
  const dow = result.getDay();
  result.setDate(result.getDate() - ((dow + 6) % 7));
  return startOfDay(result);
};

/**
 * Resolves a RelativeDateWindow into an absolute DateRange anchored to `referenceDate`.
 * Called at report-run time (scheduled send or on-demand preview) so the same saved
 * window always yields a fresh, rolling range.
 */
export const resolveRelativeDateWindow = (window: RelativeDateWindow, referenceDate: Date = new Date()): DateRange => {
  const today = endOfDay(referenceDate);

  switch (window) {
    case RelativeDateWindow.Last7Days:
      return { from: startOfDay(addDays(referenceDate, -6)), to: today };
    case RelativeDateWindow.Last14Days:
      return { from: startOfDay(addDays(referenceDate, -13)), to: today };
    case RelativeDateWindow.Last30Days:
      return { from: startOfDay(addDays(referenceDate, -29)), to: today };
    case RelativeDateWindow.LastWeek: {
      const thisWeekStart = startOfISOWeek(referenceDate);
      const lastWeekStart = addDays(thisWeekStart, -7);
      return { from: lastWeekStart, to: endOfDay(addDays(lastWeekStart, 6)) };
    }
    case RelativeDateWindow.LastMonth: {
      const lastMonthStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - 1, 1);
      const lastMonthEnd = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 0);
      return { from: startOfDay(lastMonthStart), to: endOfDay(lastMonthEnd) };
    }
    case RelativeDateWindow.LastQuarter: {
      const currentQuarter = Math.floor(referenceDate.getMonth() / 3);
      const lastQuarterStart = new Date(referenceDate.getFullYear(), (currentQuarter - 1) * 3, 1);
      const lastQuarterEnd = new Date(referenceDate.getFullYear(), currentQuarter * 3, 0);
      return { from: startOfDay(lastQuarterStart), to: endOfDay(lastQuarterEnd) };
    }
    case RelativeDateWindow.MonthToDate:
      return { from: startOfDay(new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1)), to: today };
    case RelativeDateWindow.QuarterToDate: {
      const currentQuarter = Math.floor(referenceDate.getMonth() / 3);
      return { from: startOfDay(new Date(referenceDate.getFullYear(), currentQuarter * 3, 1)), to: today };
    }
    case RelativeDateWindow.YearToDate:
      return { from: startOfDay(new Date(referenceDate.getFullYear(), 0, 1)), to: today };
    case RelativeDateWindow.CurrentYear:
      return {
        from: startOfDay(new Date(referenceDate.getFullYear(), 0, 1)),
        to: endOfDay(new Date(referenceDate.getFullYear(), 11, 31))
      };
    case RelativeDateWindow.Last2Years:
      return { from: startOfDay(addYears(referenceDate, -2, 1)), to: today };
    case RelativeDateWindow.Last5Years:
      return { from: startOfDay(addYears(referenceDate, -5, 1)), to: today };
    default:
      return { from: startOfDay(addDays(referenceDate, -6)), to: today };
  }
};
